import cx from 'classnames';
import styles from './LotteryGameRoomBody.module.scss';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { LotteryGameRoomBodyButton } from './LotteryGameRoomBodyButton/LotteryGameRoomBodyButton';
import { LotteryGameRoomBodyWaiting } from './LotteryGameRoomBodyWaiting';
import {
  LotteryInfoGameRoomState,
  setNoGamesAnimation,
} from '../../../store/lotterySlice';
import { LotteryGameRoomBodyLostConnection } from './LotteryGameRoomBodyLostConnection';
import { LotteryGameRoomBodyWinners } from './LotteryGameRoomBodyWinners';
import { useAppDispatch } from '@app/store';
import {
  useAuthControllerWhoamiQuery,
  usePhotoControllerGetUserPhotoQuery,
} from '@app/store/api/tunedRestApi';
import { vibrateImpact } from '@utils/vibrate';
import { LotteryTurnType, SuperTicketType } from '@features/LotteryGame/types';
import { FallingChips } from '@shared/FallingChips';
import { CreateNewGameProps, GameStage } from '@entities/game';
import { Typography } from '@shared/Typography';
import {
  getTicketClassName,
  getTicketLabelNode,
  isLuckyTicket,
  isMetallicTicket,
} from '@features/LotteryGame/helpers/ticketNumberUtils';
import { useAnimationsEnabled } from '@utils/hooks/useAnimationsEnabled';

const nums = new Array(50).fill(null).map((_, i) => i + 1);

interface LotteryGameRoomBodyProps extends CreateNewGameProps {
  stage: GameStage;
  animationComplete: boolean;
  lottery: LotteryInfoGameRoomState;
  luckyTicketNumber?: string;
  ticketNumber?: string;
  multiplier?: number;
}

export const LotteryGameRoomBody: FC<LotteryGameRoomBodyProps> = ({
  stage,
  lottery,
  lotteryApi,
  animationComplete,
  luckyTicketNumber,
  ticketNumber,
  multiplier,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const { data: user } = useAuthControllerWhoamiQuery();
  const { data: avatar } = usePhotoControllerGetUserPhotoQuery();
  const [countWinNums, setCountWinNums] = useState(0);
  const animationsEnabled = useAnimationsEnabled();

  const [pickedNumbers, setPickedNumbers] = useState<number[]>([]);

  const luckyTicket = isLuckyTicket(luckyTicketNumber as SuperTicketType);

  const ticketStyleClass = getTicketClassName(luckyTicketNumber as SuperTicketType);

  const isAutoplay = lottery.isAutoplay;

  const handlePickAll = useCallback(
    (pickedNumber: number) => {
      if (!lottery.picked[pickedNumber]) {
        if (lottery.picked[pickedNumber] === undefined) vibrateImpact('soft');
        lotteryApi.allPickNumber(lottery.gameRoom.id, pickedNumber);
      }
    },
    [lottery.gameRoom.id, lottery.picked, lotteryApi],
  );

  const handlePickNumber = useCallback(
    (pickedNumber: number) => {
      if (!lottery.myTurn) return;
      if (pickedNumbers.includes(pickedNumber)) return;
      if (lottery.picked[pickedNumber] === undefined) {
        const newNumbers = [...pickedNumbers, pickedNumber];
        if (newNumbers.length === 4) {
          lotteryApi.turnPickNumbers(lottery.gameRoom.id, newNumbers);
        } else {
          setPickedNumbers(newNumbers);
        }
      }

      lotteryApi.turnPickNumbers(lottery.gameRoom.id, [pickedNumber]);
    },
    [lottery.gameRoom.id, lottery.myTurn, lottery.picked, lotteryApi, pickedNumbers],
  );

  const isTurnLottery = useMemo(
    () => lottery.gameRoom.settings.turnType === LotteryTurnType.InTurn,
    [lottery.gameRoom.settings.turnType],
  );

  const handleClickNumber = useCallback(
    (num: number) => {
      if (isTurnLottery) handlePickNumber(num);
      else handlePickAll(num);
    },
    [isTurnLottery, handlePickAll, handlePickNumber],
  );

  useEffect(() => {
    if (lottery.finishState && !animationComplete) dispatch(setNoGamesAnimation(false));
    if (lottery.finishState && animationComplete) {
      setCountWinNums(0);
      dispatch(setNoGamesAnimation(true));
    }
  }, [lottery.finishState, animationComplete, dispatch]);

  const fallingChips = useMemo(
    () => (
      <div className={styles.lgrbFallingChipsContainer}>
        {[1, 2, 3, 4, 5].map((count) => (
          <FallingChips key={count} show={animationsEnabled && countWinNums === count} />
        ))}
      </div>
    ),
    [countWinNums, animationsEnabled],
  );

  if (!isAutoplay && lottery.lostConnection && !lottery.finishState)
    return <LotteryGameRoomBodyLostConnection gameStarted={lottery.gameStarted} />;

  if (!lottery.gameStarted)
    return <LotteryGameRoomBodyWaiting user={user} avatar={avatar} lottery={lottery} />;

  if (lottery.finishState && animationComplete)
    return (
      <>
        <LotteryGameRoomBodyWinners
          user={user}
          avatar={avatar}
          lotteryApi={lotteryApi}
          gameRoom={lottery.gameRoom}
          finishState={lottery.finishState}
          firstShowWinners={lottery.firstShowWinners}
          luckyTicketNumber={luckyTicketNumber}
          multiplier={multiplier}
          isAutoplay={lottery.isAutoplay}
          {...props}
        />
        <div className={styles.lgrbTicketNumbWrapper}>
          <Typography
            component="h3"
            textTransform="uppercase"
            className={cx({
              [styles.lgrbTicketNumb]:
                luckyTicketNumber &&
                !isMetallicTicket(luckyTicketNumber as SuperTicketType),
            })}
          >
            {getTicketLabelNode(luckyTicketNumber, ticketNumber)}
          </Typography>
        </div>
      </>
    );

  return (
    <>
      {fallingChips}

      <div
        className={cx(styles.lgrNumbers, {
          [styles.lgrDisableNumbers]: isTurnLottery && lottery.myTurn === false,
          [styles.lgrTouchNone]: lottery.isAutoplay,
        })}
      >
        <div className={styles[ticketStyleClass!]}></div>
        <div className={styles.lgrNumbersWrapper}>
          {nums.map((num) => {
            const myNumber = lottery.picked[num] === true;
            const anotherNumber = lottery.picked[num] === false;
            const winNumber = lottery.consecutiveWinNumbers.includes(num);

            return (
              <LotteryGameRoomBodyButton
                key={num}
                num={num}
                anotherNumber={anotherNumber}
                myNumber={myNumber}
                winNumber={winNumber}
                onClick={handleClickNumber}
                stage={stage}
                setCountWinNums={setCountWinNums}
                luckyTicketNumber={luckyTicketNumber}
              />
            );
          })}
        </div>

        {luckyTicket && multiplier && (
          <div className={styles.lgrbTicketMultiplierWrapper}>
            <Typography
              component="h3"
              textTransform="uppercase"
              className={styles.lgrbTicketMultiplier}
            >
              X{multiplier}
            </Typography>
          </div>
        )}
      </div>
    </>
  );
};
